import React, { useState, useEffect, useCallback } from 'react';
import util from '../../styles/utilities.module.css';
import { useSubmitModuleReflectionMutation } from '../../slices/credentialingApi';
import './ReflectionSubmission.css';

const ReflectionSubmission = ({ 
  moduleId, 
  minLength = 0,
  placeholder = "Try to describe in one or two sentences what you learned and how you will apply this new knowledge in your work.",
  onReflectionSubmitted,
  initialReflection = '',
  isSubmitted = false,
  submittedAt = null,
  isRequired = false
}) => {
  // Initialize with existing reflection data (if any)
  const [reflection, setReflection] = useState(initialReflection);
  const [startTime] = useState(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [characterCount, setCharacterCount] = useState(0);
  
  const [submitReflection] = useSubmitModuleReflectionMutation();

  // Handle module changes - set reflection based on module's current data
  useEffect(() => {
    // Always update reflection to match the current module's data
    // This ensures each module shows its own reflection
    setReflection(initialReflection);
  }, [moduleId, initialReflection]);

  // Calculate word and character counts
  useEffect(() => {
    const words = reflection.trim() ? reflection.trim().split(/\s+/).length : 0;
    const characters = reflection.length;
    setWordCount(words);
    setCharacterCount(characters);
  }, [reflection]);

  // Calculate time spent
  const calculateTimeSpent = useCallback(() => {
    return Math.floor((Date.now() - startTime) / 1000);
  }, [startTime]);

  // Check if reflection meets minimum requirements
  const meetsRequirements = characterCount > 0;

  // Handle reflection submission
  const handleSubmit = async () => {
    if (!meetsRequirements) {
      setSubmitError(`Please write at least ${minLength} characters for your reflection.`);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await submitReflection({
        moduleId: parseInt(moduleId),
        reflection: reflection.trim(),
        timeSpent: calculateTimeSpent()
      }).unwrap();

      if (result.success) {
        setShowSuccess(true);
        
        // Call callback if provided
        if (onReflectionSubmitted) {
          onReflectionSubmitted(result.data);
        }

        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to submit reflection:', error);
      setSubmitError(
        error.data?.error || 
        'Failed to submit reflection. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle auto-save (debounced)
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (reflection.trim() && characterCount >= 10) {
        // Auto-save reflection after user stops typing for 3 seconds
        // This is optional - you can implement auto-save to the reflection endpoint
        console.log('Auto-saving reflection...');
      }
    }, 3000);

    return () => clearTimeout(autoSaveTimer);
  }, [reflection, characterCount]);

  return (
    <div className="reflection-submission">

      {/* Read-only view for submitted reflections */}
      {isSubmitted && (
        <div className="reflection-submitted">
          <div className="submitted-header">
            <div className="submitted-icon">✅</div>
            <div className="submitted-info">
              <h4>Reflection Submitted</h4>
              <p>You submitted your reflection on {new Date(submittedAt).toLocaleDateString()} at {new Date(submittedAt).toLocaleTimeString()}</p>
            </div>
          </div>
          <div className="submitted-content">
            <h5>Your Reflection:</h5>
            <div className="reflection-readonly">
              {reflection}
            </div>
          </div>
        </div>
      )}

      {/* Only show editable interface if reflection hasn't been submitted */}
      {!isSubmitted && (
        <>
          {/* Success notification */}
          {showSuccess && (
            <div className="reflection-success">
              <div className="success-icon">✅</div>
              <div className="success-message">
                <div>Reflection submitted successfully!</div>
                <div>Thank you for sharing your insights.</div>
              </div>
            </div>
          )}

          {/* Error notification */}
          {submitError && (
            <div className="reflection-error">
              <div className="error-icon">⚠️</div>
              <div className="error-msg">{submitError}</div>
              <button 
                className={`${util['btn-icon']} ${util['btn-icon-sm']} error-dismiss`}
                onClick={() => setSubmitError(null)}
                aria-label="Dismiss error"
              >
                ×
              </button>
            </div>
          )}

          {/* Reflection input area */}
          <div className="reflection-input-container">
        <textarea
          className={`reflection-textarea ${!meetsRequirements && characterCount > 0 ? 'insufficient' : ''}`}
          value={reflection}
          onChange={(e) => setReflection(e.target.value)}
          placeholder={placeholder}
          disabled={isSubmitting}
          rows={8}
        />
        
        {/* Character counter and progress */}
        <div className="reflection-stats">
          <div className="character-info">
            <div className="character-count">
              {/* <span className={characterCount >= minLength ? 'sufficient' : 'insufficient'}>
                {characterCount}
              </span>
              <span className="min-length">/{minLength} characters</span> */}
              {/* <span className="word-count">({wordCount} words)</span> */}
            </div>
            
            {/* {meetsRequirements && (
              <div className="requirements-met">
                <span className="checkmark">✓</span>
                <span>Minimum length requirement met</span>
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Submission controls */}
      <div className="reflection-controls">
        <div className="submission-info">
          {isRequired ? (
            <span className="required-text">
              This reflection is required to complete the module
            </span>
          ) : (
            <span className="optional-text">
              Submitting a reflection is not required, but we encourage you to write down your thoughts!
            </span>
          )}
        </div>
        
        <button
          className={`${util.btn} ${meetsRequirements ? util['btn--primaryGradient'] + ' ' + util.elevateHover : util['btn--ghost']} reflectionSubmitBtn`}
          onClick={handleSubmit}
          disabled={!meetsRequirements || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner"></span>
              Submitting...
            </>
          ) : (
            'Submit Reflection'
          ) 
        }
        </button>
      </div>

         
        </>
      )}
    </div>
  );
};

export default ReflectionSubmission;
